import type { PackageJson, PackageSettings } from '@ms-cloudpack/common-types';
import { deepFreeze } from './deepFreeze.js';

export interface BuiltInSettings {
  /**
   * Package settings to automatically apply for this package name/version.
   * (These are merged by `getPackageSettings`, mostly as called by `createPackageSettingsTransform`.)
   */
  packageSettings?: Omit<PackageSettings, 'match' | 'exports'>;
  /**
   * package.json settings to automatically apply for this package name/version.
   * These **overwrite** original properties of the `package.json` and are applied by
   * `PackageDefinitions` using `createBuiltInSettingsTransform`.
   */
  packageJson?: Omit<PackageJson, 'name' | 'version' | 'cloudpack'>;
}

/**
 * Mapping from package name to version range to built-in settings.
 *
 * This object is frozen to prevent accidental modification.
 *
 * The reason we're handling package.json modifications (just `exports` as of writing) and
 * `PackageSettings` separately is mainly to get around issues with merging exports maps.
 * https://github.com/microsoft/cloudpack/pull/2452#discussion_r1835192676
 * (If the exports merging issue is fixed in the future, and we don't find any cases where we want
 * to override other package.json properties, they could potentially be combined and handled with
 * only package settings.)
 */
export const builtInSettings = deepFreeze<
  Partial<{
    [packageName: string]: {
      [versionRange: string]: BuiltInSettings;
    };
  }>
>({
  '@fluentai/flair': {
    '>=0 <1.0.0': {
      // Use rollup as esbuild translates classes incorrectly
      packageSettings: { bundler: 'rollup' },
    },
  },
  '@fluentui/react-bindings': {
    '>=0 <1.0.0': {
      // Use rollup to preserve export *
      packageSettings: { bundler: 'rollup' },
    },
  },
  '@fluentui/react-northstar': {
    '>=0 <1.0.0': {
      // Use rollup to preserve export *
      packageSettings: { bundler: 'rollup' },
    },
  },
  '@fluid-internal/client-utils': {
    '>=0 <3.0.0': {
      // This package mixes CJS and ESM which causes problems for ori.
      packageSettings: { bundler: 'rollup' },
    },
  },

  '@microsoft/objectstoreprovider': {
    '>=0 <1.0.0': {
      // It is a CJS package with a declared side effect, we need webpack/rspack to respect it.
      packageSettings: {
        bundler: 'rspack',
      },
    },
  },

  '@microsoft/recognizers-text-date-time': {
    '^1.0.0': {
      // This has a "browser" entry which doesn't provide exports correctly in the browser when bundled??
      // Switching to the regular entry point makes it work.
      // https://unpkg.com/browse/@microsoft/recognizers-text-date-time@1.3.2/
      packageJson: {
        exports: './dist/recognizers-text-date-time.es5.js',
      },
    },
  },

  asap: {
    '^2.0.0': {
      // This package has different browser and non-browser versions in the "browser" field.
      // Trying to bundle it with rollup (which creates an ESM stub) gives this error:
      //   [ESM stub generation] /<repo path>/node_modules/asap/browser-asap.js:10:33
      //     Error creating stub for entry "./browser-asap":
      //     TypeError: rawAsap.makeRequestCallFromTimer is not a function
      // It's not clear why this happens, but bundling this CJS package with ori works okay
      // since the package doesn't require() any dependencies.
      // https://unpkg.com/browse/asap@2.0.6/
      packageSettings: {
        bundler: 'ori',
      },
    },
  },

  brotli: {
    '^1.0.0': {
      // index.js also imports a compress entry point with a reference to nonexistent Browser global
      // https://unpkg.com/browse/brotli@1.3.3/
      packageJson: {
        exports: {
          './decompress.js': './decompress.js',
        },
      },
    },
  },

  bson: {
    '^5.0.0 || ^6.0.0': {
      // Package conditionally references "crypto", causing an error even though it's never used.
      // Bundle with rollup instead, and ignore the warning about the empty crypto stub.
      packageSettings: {
        bundler: 'rollup',
        ignoredBundlerWarnings: ['Generated an empty chunk: "_polyfill-node.crypto"'],
      },
    },
  },

  color: {
    '>=0 <6.0.0': {
      packageSettings: {
        // This package iterates through the keys of "color-convert" and errors on a default key.
        // It is a small package so we can inline all its dependencies and use ori instead of rollup.
        inlinedDependencies: ['color-convert', 'color-string'],
        excludedDependencies: ['color-convert', 'color-string'],
        bundler: 'ori',
      },
    },
  },

  'gql.tada': {
    '^1.0.0': {
      // This package has a combination of browser and CLI code.
      // Remove the dependencies and entries which aren't needed in the browser.
      // https://unpkg.com/browse/gql.tada@1.8.10/
      packageSettings: {
        excludedDependencies: ['@0no-co/graphqlsp', '@gql.tada/cli-utils', '@gql.tada/internal', 'typescript'],
      },
      packageJson: {
        exports: './dist/gql-tada.mjs',
      },
    },
  },

  'html-dom-parser': {
    '>=0 <6.0.0': {
      // Old versions of this package are CJS,
      // and newer versions have an ESM entry that imports CJS code with `require`.
      // This causes the runtime issue "Dynamic require of {package} is not supported" when bundling with ori,
      // so we need to use rollup instead.
      packageSettings: {
        bundler: 'rollup',
      },
    },
  },

  'html-react-parser': {
    // Old versions of this package are CJS,
    // and newer versions have an ESM entry that imports CJS code with `require`.
    // This causes the runtime issue "Dynamic require of {package} is not supported" when bundling with ori,
    // so we need to use rollup instead.
    '>=0 <6.0.0': {
      packageSettings: {
        bundler: 'rollup',
      },
    },
  },

  htmlparser2: {
    // Issue below starts in 8.0.1 and continues in 9
    '^8.0.1 || ^9.0.0': {
      // This has an additional ./lib/WritableStream exports map entry which uses node stream
      // https://unpkg.com/browse/htmlparser2@9.1.0/
      packageJson: {
        exports: './lib/esm/index.js',
      },
    },
  },

  'office-ui-fabric-react': {
    '>=0 <8.0.0': {
      // Use rollup to preserve export *
      packageSettings: { bundler: 'rollup' },
    },
  },

  postcss: {
    // The package added a .mjs as a stub. This confuses cloudpack and we try to bundle with ori and fail
    '^8.0.0': {
      packageJson: {
        exports: './lib/postcss.js',
      },
    },
  },

  react: {
    '^16.14.0 || ^17.0.0': {
      // Package is missing an exports map
      // https://unpkg.com/browse/react@17/package.json
      packageJson: {
        exports: {
          '.': './index.js',
          './jsx-runtime': './jsx-runtime.js',
          './jsx-dev-runtime': './jsx-dev-runtime.js',
        },
      },
    },
  },

  scheduler: {
    '>=0 <0.21.0': {
      // Package is missing an exports map
      // https://unpkg.com/browse/scheduler@0.20.x/package.json
      packageJson: {
        exports: {
          '.': './index.js',
          './tracing': './tracing.js',
        },
      },
    },
    '>=0.21.0 <1.0.0': {
      // Package is missing an exports map
      // https://unpkg.com/browse/scheduler@0.21.0/package.json
      packageJson: {
        exports: './index.js',
      },
    },
  },

  'socket.io-client': {
    '^4.4.1': {
      // Remove unneeded exports
      // https://unpkg.com/browse/socket.io-client@4/package.json
      packageJson: {
        exports: './build/esm/index.js',
      },
    },
  },

  'style-to-object': {
    // Old versions of this package are CJS,
    // and newer versions have an ESM entry that imports CJS code with `require`.
    // This causes the runtime issue "Dynamic require of {package} is not supported" when bundling with ori,
    // so we need to use rollup instead.
    '>=0 <2.0.0': {
      // Package is
      packageSettings: {
        bundler: 'rollup',
      },
    },
  },

  tslib: {
    '>=0 <2.5.3': {
      // Remove unneeded exports
      // https://unpkg.com/browse/tslib@2.5.2/package.json
      packageJson: {
        exports: './tslib.es6.js',
      },
    },
    '^2.5.3': {
      // Remove unneeded exports
      // https://unpkg.com/browse/tslib@2.5.3/package.json
      packageJson: {
        exports: './tslib.es6.mjs',
      },
    },
  },

  // Please add new entries in alphabetical order by name!
});
